// Puts a resume's dated entries (experience and education) in date order, the latest first. The dates are free text a person typed ("Jan 2022",
// "January 2022", "01/2022", "2022-01", "2022", "Mär 2021"). This reads them the way the server does (app/services/resume_dates.rb: change both
// together), so what the editor shows and what is saved agree.
//
// An entry is placed by when it ended, then by when it started. One that is current ("I work here now", or an end of "Present") counts as ending
// now, so it comes first. An entry with no readable date keeps its place after the dated ones, in the order it had.

// The first three letters of a month in English, German, French and Spanish. "juin" and "juillet" share them, so those two are whole words.
const MONTHS = {
  jan: 1, ene: 1, feb: 2, fev: 2, mar: 3, apr: 4, avr: 4, abr: 4, may: 5, mai: 5, jun: 6,
  jul: 7, aug: 8, aou: 8, ago: 8, sep: 9, set: 9, oct: 10, okt: 10, nov: 11, dec: 12, dez: 12, dic: 12,
}
const WHOLE = { juin: 6, juillet: 7 }
const PRESENT = /^(present|current|currently|now|today|ongoing|heute|aktuell|gegenwart|jetzt|actuel|actuellement|presente|actualidad)$/

const monthOf = (year, month) => {
  const number = Number(month)
  return number >= 1 && number <= 12 ? Number(year) * 12 + number - 1 : null
}

// The month named in the text ("jan", "september", "mar."), or null.
function monthWord(clean) {
  for (const word of clean.match(/[a-z]+/g) || []) {
    if (WHOLE[word]) return WHOLE[word]
    if (word.length >= 3 && MONTHS[word.slice(0, 3)]) return MONTHS[word.slice(0, 3)]
  }
  return null
}

// Months since year 0 (year * 12 + month - 1), or null when no year can be read. A year on its own is January as a start and December as an end.
export function parseDate(text, edge = 'start') {
  const clean = String(text ?? '').normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase().replace(/,/g, ' ').replace(/\s+/g, ' ').trim()
  if (!clean) return null

  let match = clean.match(/^(\d{4})-(\d{1,2})(?:-\d{1,2})?$/)
  if (match) return monthOf(match[1], match[2])
  match = clean.match(/^(\d{1,2})\s*[/.]\s*(\d{4})$/)
  if (match) return monthOf(match[2], match[1])
  match = clean.match(/^\d{1,2}\s*[/.]\s*(\d{1,2})\s*[/.]\s*(\d{4})$/)
  if (match) return monthOf(match[2], match[1])
  match = clean.match(/(?<!\d)((?:19|20)\d{2})(?!\d)/)
  if (match) return monthOf(match[1], monthWord(clean) || (edge === 'end' ? 12 : 1))
  return null
}

// [when it ended, when it started] as numbers, or null when the entry has no readable date.
export function dateKey(entry) {
  const start = parseDate(entry.start_date, 'start')
  const ended = entry.current === true || PRESENT.test(String(entry.end_date ?? '').trim().toLowerCase()) ? Infinity : parseDate(entry.end_date, 'end')
  const end = ended ?? start
  return end === null ? null : [end, start ?? end]
}

// A new array in date order (the entries themselves are not changed).
export function sortByDate(entries) {
  const keyed = entries.map((entry, index) => ({ entry, index, key: dateKey(entry) }))
  const dated = keyed.filter((item) => item.key).sort((a, b) => (b.key[0] - a.key[0]) || (b.key[1] - a.key[1]) || (a.index - b.index))
  return [...dated, ...keyed.filter((item) => !item.key)].map((item) => item.entry)
}

// True when the entries are not in date order yet.
export const needsSorting = (entries) => sortByDate(entries).some((entry, index) => entry !== entries[index])
